// pages/contentManagement/quotes/quoteList.js
import React, { useEffect, useReducer, useMemo, useCallback, useState } from "react";
import { Button, Collapse, Divider, Form, Input, List, Modal, Pagination, Spin } from "antd";
import { FaEdit, FaTrash, FaChevronDown } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./quoteList.css";

const { TextArea } = Input;
const { Panel } = Collapse;

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  motivations: [],
  currentPage: 1,
  pageSize: 5,
  sortConfig: { key: "createdDate", direction: "desc" },
  showDeleteModal: false,
  deleteMotivationId: null,
  editingMotivation: null
};

// Reducer for state management
const motivationReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setMotivations":
      return { ...state, motivations: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "setDeleteModal":
      return {
        ...state,
        showDeleteModal: action.payload.show,
        deleteMotivationId: action.payload.motivationId || null
      };
    case "setEditingMotivation":
      return { ...state, editingMotivation: action.payload };
    case "removeMotivation":
      return {
        ...state,
        motivations: state.motivations.filter((motivation) => motivation.id !== action.payload)
      };
    case "addMotivation":
      return {
        ...state,
        motivations: [action.payload, ...state.motivations]
      };
    case "updateMotivation":
      return {
        ...state,
        motivations: state.motivations.map((motivation) => (motivation.id === action.payload.id ? action.payload : motivation))
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1
      };
    default:
      return state;
  }
};

function QuoteManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(motivationReducer, initialState);
  const [form] = Form.useForm();

  // Additional state for form and notifications
  const [submitLoading, setSubmitLoading] = useState(false);

  const { isLoading, searchTerm, filters, motivations, currentPage, pageSize, sortConfig, showDeleteModal, deleteMotivationId, editingMotivation } = state;

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      sortOrder: {
        label: "Sort By",
        options: [
          { value: "newest", label: "Newest First" },
          { value: "oldest", label: "Oldest First" },
          { value: "authorAsc", label: "Author (A-Z)" },
          { value: "authorDesc", label: "Author (Z-A)" }
        ]
      }
    }),
    []
  );

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [item.author || "", item.motivationalText || ""];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters and search
  const filteredData = useMemo(() => {
    let result = [...motivations];

    // Apply sorting
    if (filters.sortOrder) {
      switch (filters.sortOrder) {
        case "newest":
          result.sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0));
          break;
        case "oldest":
          result.sort((a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0));
          break;
        case "authorAsc":
          result.sort((a, b) => (a.author || "").localeCompare(b.author || ""));
          break;
        case "authorDesc":
          result.sort((a, b) => (b.author || "").localeCompare(a.author || ""));
          break;
        default:
          break;
      }
    } else if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === "createdDate") {
          return direction === "asc" ? new Date(a.createdDate || 0) - new Date(b.createdDate || 0) : new Date(b.createdDate || 0) - new Date(a.createdDate || 0);
        }
        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    // Apply global search
    result = globalSearch(result, searchTerm);

    return result;
  }, [motivations, filters, searchTerm, globalSearch, sortConfig]);

  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Fetch Motivations
  const fetchMotivations = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });
    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.MOTIVATION, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 && Array.isArray(response.data)) {
        dispatch({ type: "setMotivations", payload: response.data });
      } else {
        throw new Error("Unexpected API response format.");
      }
    } catch (error) {
      console.error("Error fetching motivations:", error);
      showNotification("Failed to load quotes", "error");

      // Fallback to static data for demo purposes
      const staticMotivations = [
        {
          id: 1,
          author: "Jim Elliot",
          motivationalText: "He is no fool who gives what he cannot keep to gain that which he cannot lose.",
          createdDate: "2025-01-15"
        },
        {
          id: 2,
          author: "C.S. Lewis",
          motivationalText: "You are never too old to set another goal or to dream a new dream.",
          createdDate: "2025-01-16"
        },
        {
          id: 3,
          author: "Corrie ten Boom",
          motivationalText: "Worry does not empty tomorrow of its sorrow, it empties today of its strength.",
          createdDate: "2025-01-17"
        }
      ];
      dispatch({ type: "setMotivations", payload: staticMotivations });
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [showNotification]);

  // Handle Submit (Add or Update)
  const handleSubmit = useCallback(
    async (values) => {
      setSubmitLoading(true);

      const motivationData = {
        author: values.author,
        motivationalText: values.motivationalText
      };

      try {
        const token = await getAuthToken();

        if (editingMotivation) {
          // PUT/PATCH Request for Updating Motivation
          await axios.patch(`${API_ENDPOINTS.MOTIVATION}/${editingMotivation.id}`, motivationData, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          });
          showNotification("Quote updated successfully!", "success");
        } else {
          // POST Request for Adding Motivation
          await axios.post(API_ENDPOINTS.MOTIVATION, motivationData, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          });
          showNotification("Quote added successfully!", "success");
        }

        // Reset form and refresh data
        form.resetFields();
        dispatch({ type: "setEditingMotivation", payload: null });
        await fetchMotivations();
      } catch (error) {
        console.error("Error saving motivation:", error.response?.data || error.message);
        showNotification(error.response?.data?.message || "Failed to save quote. Please try again.", "error");
      } finally {
        setSubmitLoading(false);
      }
    },
    [editingMotivation, fetchMotivations, showNotification, form]
  );

  // Handle Edit
  const handleEdit = useCallback(
    (motivation) => {
      dispatch({ type: "setEditingMotivation", payload: motivation });
      form.setFieldsValue({
        author: motivation.author,
        motivationalText: motivation.motivationalText
      });
      // Scroll to form
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [form]
  );

  // Handle Cancel - Clear form
  const handleCancel = useCallback(() => {
    form.resetFields();
    dispatch({ type: "setEditingMotivation", payload: null });
    showNotification("Form cleared.", "info");
  }, [form, showNotification]);

  // Handle Delete
  const confirmDelete = useCallback((id) => {
    dispatch({
      type: "setDeleteModal",
      payload: { show: true, motivationId: id }
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteMotivationId) return;

    dispatch({ type: "setDeleteModal", payload: { show: false } });
    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAuthToken();
      await axios.delete(`${API_ENDPOINTS.MOTIVATION}/${deleteMotivationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      dispatch({ type: "removeMotivation", payload: deleteMotivationId });
      showNotification("Quote deleted successfully.", "success");
    } catch (error) {
      console.error("Error deleting motivation:", error);
      showNotification("Failed to delete quote.", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [deleteMotivationId, showNotification]);

  const handleDeleteModalCancel = useCallback(() => {
    dispatch({ type: "setDeleteModal", payload: { show: false } });
  }, []);

  // Event handlers
  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((motivation, index) => ({
      "SL. NO.": index + 1,
      Quote: motivation.motivationalText,
      Author: motivation.author
    }));
  }, [filteredData]);

  // Effects
  useEffect(() => {
    fetchMotivations();
  }, [fetchMotivations]);

  return (
    <Layout
      navTitle="Content Management"
      pageTitle="Quotes"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="motm-upload-section">
        <Collapse
          expandIcon={({ isActive }) => (
            <FaChevronDown
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s"
              }}
            />
          )}
          className="motm-upload-collapse"
        >
          <Panel header={editingMotivation ? "Edit Quote" : "Add New Quote"} key="1">
            <Form form={form} layout="vertical" onFinish={handleSubmit} className="motm-upload-form">
              <Form.Item label="Motivational Quote:" name="motivationalText" rules={[{ required: true, message: "Please enter quote content" }]}>
                <TextArea className="motm-upload-input" placeholder="Enter motivational quote" rows={4} />
              </Form.Item>

              <Form.Item label="Author:" name="author" rules={[{ required: true, message: "Please enter author name" }]}>
                <Input className="motm-upload-input" placeholder="Enter author name" />
              </Form.Item>

              <Form.Item>
                <div className="motm-form-btn-group">
                  <Button className="motm-form-cancel-btn" onClick={handleCancel} disabled={submitLoading}>
                    Clear
                  </Button>
                  <Button className="motm-form-submit-btn" htmlType="submit" loading={submitLoading} disabled={submitLoading}>
                    {editingMotivation ? "Update Quote" : "Upload Quote"}
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Panel>
        </Collapse>
      </div>

      <Divider />

      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search quotes..."
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="motivational-quotes"
            excelSheetName="Quotes"
          />
        }
      >
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin />
          </div>
        ) : (
          <List
            className="tpl-list"
            dataSource={currentPageData}
            locale={{ emptyText: "No quotes available." }}
            renderItem={(motivation) => (
              <List.Item key={motivation.id} className="tpl-list-item">
                <h3>{motivation.motivationalText}</h3>
                <p>- {motivation.author}</p>
                <div className="motm-list-btn-group">
                  <Button className="motm-list-edit-btn" icon={<FaEdit />} onClick={() => handleEdit(motivation)}>
                    Edit
                  </Button>
                  <Button className="motm-list-delete-btn" icon={<FaTrash />} onClick={() => confirmDelete(motivation.id)}>
                    Delete
                  </Button>
                </div>
              </List.Item>
            )}
          />
        )}
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onCancel={handleDeleteModalCancel}
        footer={[
          <div className="motm-modal-btn-group" key="modal-buttons">
            <Button className="motm-modal-cancel-btn" key="cancel" onClick={handleDeleteModalCancel}>
              Cancel
            </Button>
            <Button className="motm-modal-delete-btn" key="delete" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        ]}
        width={400}
        centered
      >
        <p>Are you sure you want to delete this quote?</p>
      </Modal>
    </Layout>
  );
}

export default QuoteManagement;
